import EverTrue from 'app';
import { getLinkUrl } from 'entities/helpers/hardcodedLinks';
import ErrorLogger from 'entities/helpers/error-log-helper';

const thankviewApiCall = async (method, endpoint, data = null, token, isFormData = false) => {
  const url = `https://thankview.com/api/${endpoint}`;
  let options = {
    method: method,
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  if (data) {
    if (isFormData) {
      if (!(data instanceof FormData)) {
        // If isFormData is true but data is not FormData, create a new FormData object
        const formData = new FormData();
        Object.entries(data).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            if (typeof value === 'boolean') {
              formData.append(key, value ? 'true' : 'false');
            } else if (value instanceof File) {
              formData.append(key, value, value.name);
            } else {
              formData.append(key, value.toString());
            }
          }
        });
        options.body = formData;
      } else {
        options.body = data;
      }
      // Don't set Content-Type header for FormData, let the browser handle it
    } else {
      // If not FormData, stringify the data and set Content-Type to JSON
      options.body = JSON.stringify(data);
      options.headers['Content-Type'] = 'application/json';
    }
  }

  try {
    const response = await fetch(url, options);

    if (!response.ok) {
      const errorText = await response.text();
      let errorMessage;
      try {
        const errorJson = JSON.parse(errorText);
        errorMessage = errorJson.message || 'API request failed';
      } catch {
        errorMessage = errorText || 'API request failed';
      }
      EverTrue.Alert.error(errorMessage);
    }

    // Check if the response is JSON
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      return await response.json();
    } else {
      // If not JSON, return the raw response
      return await response.text();
    }
  } catch (error) {
    EverTrue.Alert.error(error);
  }
};

const fetchSignalIndex = async (data, token) => {
  try {
    return await thankviewApiCall('POST', 'signal/personal/index', data, token);
  } catch (error) {
    EverTrue.Alert.error(error);
  }
};
const setBusinessLogo = async (id, file, token) => {
  const url = getLinkUrl('thankView', 'setBusinessLogoUrl');
  try {
    const formData = new FormData();
    formData.append('logo_image', file);
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      redirect: 'error',
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(JSON.stringify(errorData));
    }

    return await response.json();
  } catch (error) {
    EverTrue.Alert.error(error);
  }
};

// const getBusinessLogo = async (id, token) => {
//   try {
//     return await thankviewApiCall('GET', `signal/business/${id}/logo`, null, token);
//   } catch (error) {
//     EverTrue.Alert.error(error);
//   }
// };

// const removeBusinessLogo = async (id, token) => {
//   try {
//     return await thankviewApiCall('DELETE', `signal/business/${id}/logo`, null, token);
//   } catch (error) {
//     EverTrue.Alert.error(error);
//   }
// };

const createAICaptions = async (id, token) => {
  try {
    return await thankviewApiCall('POST', `signal/captions/${id}/ai`, null, token);
  } catch (error) {
    EverTrue.Alert.error('There was an error creating your captions');
    ErrorLogger.error('Error creating video captions', { extra: { error: error, page_name: 'SignalVideo' } });
  }
};

const pollAiCaptions = async (id, token) => {
  try {
    return await thankviewApiCall('GET', `signal/captions/ai/poll/${id}`, null, token);
  } catch (error) {
    ErrorLogger.error('Error polling video captions', { extra: { error: error, page_name: 'SignalVideo' } });
  }
};

const updateCaptions = async (id, token) => {
  try {
    return await thankviewApiCall('PUT', `signal/captions/${id}`, null, token);
  } catch (error) {
    EverTrue.Alert.error(error);
  }
};

const updateCaptionsFile = async (id, data, token) => {
  try {
    if (!(data instanceof FormData)) {
      throw new Error('Data must be an instance of FormData');
    }
    const response = await thankviewApiCall('POST', `signal/captions/${id}`, data, token, true);
    return response;
  } catch (error) {
    EverTrue.Alert.error(error);
  }
};

const setAutoplayCaptions = async (id, data, token) => {
  //{   "autoplay_captions": true }
  try {
    return await thankviewApiCall('PUT', `signal/captions/autoplay/${id}`, null, token);
  } catch (error) {
    EverTrue.Alert.error(error);
  }
};

const destroyCaptions = async (id, token) => {
  try {
    return await thankviewApiCall('DELETE', `signal/captions/${id}`, null, token);
  } catch (error) {
    EverTrue.Alert.error(error);
  }
};

const setLandingPageColors = async (id, data, token) => {
  //{   "bkgd_color": "#ffffff",   "button_color": "#000000" }
  try {
    return await thankviewApiCall('POST', `signal/landing/${id}`, null, token);
  } catch (error) {
    EverTrue.Alert.error(error);
  }
};
const pollVideoStatus = async (id, token) => {
  try {
    return await thankviewApiCall('GET', `signal/video/${id}/poll`, null, token);
  } catch (error) {
    EverTrue.Alert.error(error);
  }
};

// poll trim
const pollVideoTrimStatus = async (libraryVideoId, token) => {
  try {
    return await thankviewApiCall('GET', `signal/library/${libraryVideoId}/trim/poll`, null, token);
  } catch (error) {
    EverTrue.Alert.error(`Error with polling for videoId ${libraryVideoId} `);
  }
};

const pollAnimatedThumbnail = async (id, token) => {
  try {
    return await thankviewApiCall('GET', `signal/animatedThumbnail/${id}/poll`, null, token);
  } catch (error) {
    EverTrue.Alert.error(`Error with polling for videoId ${id} `);
  }
};

const pollAnimatedThumbnailUntilFinished = async (videoId, token) => {
  const startTime = Date.now();
  const timeoutDuration = 15000; // 15 seconds timeout

  while (Date.now() - startTime < timeoutDuration) {
    const { data } = await pollAnimatedThumbnail(videoId, token);

    if (data?.status === 'finished') {
      return data;
    }
    await new Promise((resolve) => setTimeout(resolve, 1000));
  }

  console.warn('Animated thumbnail polling timed out after 15 seconds');
  return { status: 'failed', animatedThumbnailPath: '' };
};

const fetchVideos = async (token) => {
  try {
    return await thankviewApiCall('GET', 'signal/video', null, token);
  } catch (error) {
    EverTrue.Alert.error(error);
  }
};

const updateVideo = async (id, data, token) => {
  delete data.logo_url;
  const updateSettings = {
    personalVideo: JSON.stringify(data),
  };
  try {
    return await thankviewApiCall('POST', `signal/personal/update/${id}`, updateSettings, token);
  } catch (error) {
    EverTrue.Alert.error(error);
  }
};

const trimVideo = async (libraryVideoId, data, token) => {
  try {
    return await thankviewApiCall('PUT', `signal/library/${libraryVideoId}/trim`, data, token);
  } catch (error) {
    EverTrue.Alert.error(error);
  }
};

const revertTrim = async (libraryVideoId, token) => {
  try {
    return await thankviewApiCall('PUT', `signal/library/${libraryVideoId}/trim/revert`, null, token);
  } catch (error) {
    EverTrue.Alert.error(error);
  }
};

const deleteVideo = async (id, token) => {
  try {
    await thankviewApiCall('DELETE', `signal/personal/${id}`, null, token);
  } catch (error) {
    EverTrue.Alert.error(`Error deleting ${id}, ${error.message}`);
  }
};

const setDefaultButtonOptions = async (buttonOptions, token) => {
  const url = getLinkUrl('thankView', 'setDefaultButtonOptionsUrl');

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(buttonOptions),
      redirect: 'error',
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(JSON.stringify(errorData));
    }
    return await response.json();
  } catch (error) {
    EverTrue.Alert.error(`Error setting default button options ${error}`);
  }
};
const setDefaultLandingPageOptions = async (id, token) => {
  //{   "landing_page_id": 1 }
  try {
    await thankviewApiCall('POST', 'signal/personal/setDefaultLandingPage', null, token);
  } catch (error) {
    EverTrue.Alert.error(`Error setting default landing page options ${error.message}`);
  }
};
const setDefaultEnvelopeOptions = async (id, token) => {
  //{   "envelope_id": 1 }
  try {
    await thankviewApiCall('POST', 'signal/personal/setDefaultEnvelope', null, token);
  } catch (error) {
    EverTrue.Alert.error(`Error setting default envelope options: ${error.message}`);
  }
};
const setLandingPageOptions = async (id, token) => {
  try {
    await thankviewApiCall('POST', `signal/personal/landing/${id}`, null, token);
  } catch (error) {
    EverTrue.Alert.error(`Error setting landing page options for ${id}`);
  }
};
const setEnvelopeOptions = async (id, token) => {
  try {
    await thankviewApiCall('POST', `signal/personal/envelope/${id}`, null, token);
  } catch (error) {
    EverTrue.Alert.error(`Error setting envelope options for ${id}`);
  }
};

const bulkDelete = async (data, token) => {
  const url = getLinkUrl('thankView', 'bulkDeleteUrl');
  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(data),
      redirect: 'error',
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(JSON.stringify(errorData));
    }

    return await response.json();
  } catch (error) {
    EverTrue.Alert.error('Error bulk deleting videos');
  }
};

const getMetrics = async (id, token) => {
  try {
    await thankviewApiCall('GET', `signal/personal/metrics/${id}`, null, token);
  } catch (error) {
    EverTrue.Alert.error('Issue on retrieving metrics');
  }
};

const getUserDefaultSettings = async (token) => {
  try {
    return await thankviewApiCall('GET', `signal/personal/user/defaultSettings`, null, token);
  } catch (error) {
    console.error(error);
  }
};

const setPersonalVideoDefaultSettings = async (settings, token) => {
  try {
    return await thankviewApiCall('POST', 'signal/personal/user/setDefaultSettings', settings, token);
  } catch (error) {
    EverTrue.Alert.error('Error setting personal video default settings');
  }
};

const setPersonalVideoDefaultLogo = async (file, token, id) => {
  try {
    const formData = new FormData();
    formData.append('logo_image', file);
    if (id) {
      formData.append('video_id', id);
    }
    return await thankviewApiCall('POST', 'signal/personal/user/setDefaultLogo', formData, token, true);
  } catch (error) {
    EverTrue.Alert.error('Error setting default logo');
  }
};

const setPersonalVideoLogo = async (file, id, token) => {
  try {
    const formData = new FormData();
    formData.append('logo_image', file);
    formData.append('video_id', id);
    return await thankviewApiCall('POST', `signal/personal/settings/logo/${id}`, formData, token, true);
  } catch (error) {
    EverTrue.Alert.error('Error setting personal video logo');
  }
};

const updateVideoSettings = async (id, data, token) => {
  const updateSettings = {
    personalVideoSettings: { logo: null },
  };
  try {
    return await thankviewApiCall('PUT', `signal/personal/settings/update/${id}`, updateSettings, token);
  } catch (error) {
    EverTrue.Alert.error(error);
  }
};

const uploadVideoHelper = async (formData, token = null) => {
  const url = getLinkUrl('thankView', 'uploadVideo');

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: formData,
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(
      `Failed to upload video: ${response.status} ${response.statusText}. Server response: ${JSON.stringify(errorData)}`
    );
  }
  const { data } = await response.json();
  return data;
};

export {
  thankviewApiCall,
  fetchSignalIndex,
  setBusinessLogo,
  //   getBusinessLogo,
  //   removeBusinessLogo,
  createAICaptions,
  updateCaptions,
  updateCaptionsFile,
  setAutoplayCaptions,
  pollVideoStatus,
  pollVideoTrimStatus,
  pollAnimatedThumbnailUntilFinished,
  pollAiCaptions,
  fetchVideos,
  updateVideo,
  trimVideo,
  revertTrim,
  deleteVideo,
  destroyCaptions,
  setLandingPageColors,
  setLandingPageOptions,
  setDefaultLandingPageOptions,
  setDefaultButtonOptions,
  setDefaultEnvelopeOptions,
  setEnvelopeOptions,
  bulkDelete,
  getMetrics,
  getUserDefaultSettings,
  setPersonalVideoDefaultSettings,
  setPersonalVideoDefaultLogo,
  setPersonalVideoLogo,
  updateVideoSettings,
  uploadVideoHelper,
};
